<template>
  <div class="flex justify-between gap-8">
    <div
      v-if="selectedOffer.merchant"
      class="text-static-default-low text-caption mb-16"
      :class="{ 'flex flex-wrap': shouldDisplaySellerFlag }"
    >
      <FormattedMessage
        :definition="
          category !== 'Refurbished'
            ? translations.resultSellerNew
            : translations.resultSeller
        "
        tag="span"
      >
        <template #seller>
          <RevLink class="truncate" :to="sellerpageUrl" @click="onSellerClick">
            {{ selectedOffer.merchant.company }}
          </RevLink>
        </template>
      </FormattedMessage>

      <Flag
        v-if="shouldDisplaySellerFlag"
        :aria-label="selectedOffer.merchant.country.name"
        class="ml-8 shrink-0"
        :country-code="selectedOffer.merchant.country.code as MarketCountryCode"
      />

      <span v-if="shouldDisplaySellerCountry" class="ml-4 shrink-0">
        ({{ selectedOffer.merchant.country.name }})
      </span>
    </div>
    <RevLink
      v-if="marketplace.marketplace.marketplaceCode === MarketplaceCode.EU"
      class="text-action-default-hi caption-link h-fit shrink-0 pt-4"
      @click="openModal(MODAL_NAMES.SELLER_DETAILS)"
    >
      {{ i18n(translations.sellerDetails) }}
    </RevLink>
  </div>
  <SellerDetailsModal
    v-if="marketplace.marketplace.marketplaceCode === MarketplaceCode.EU"
    :listing-id="selectedOffer?.offerId"
  />
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { MarketplaceCode } from '@backmarket/http-api'
import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import type { GetPickersResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevLink } from '@ds/components/Link'
import { openModal } from '@ds/components/ModalBase'

import { useRouteLocationWithLocale } from '~/composables/useRouteLocationWithLocale'
import { MODAL_NAMES } from '~/scopes/product/constants'
import { ROUTES as PRODUCT_LIST_ROUTES } from '~/scopes/product-list/route-names'

import translations from './Seller.translations'
import SellerDetailsModal from './components/SellerDetailsModal/SellerDetailsModal.vue'

const i18n = useI18n()
const { trackClick } = useTracking()
const marketplace = useMarketplace()
const resolveLocalizedRoute = useRouteLocationWithLocale()

const props = defineProps<{
  selectedOffer: NonNullable<GetPickersResponse['selectedOffer']>
  category: GetProductResponse['type']
}>()

const shouldDisplaySellerFlag = computed(() => {
  return (
    !!props.selectedOffer.merchant &&
    !!props.selectedOffer.merchant.country.code &&
    marketplace.market.countryCode !== MarketCountryCode.FR &&
    props.selectedOffer.merchant.country.code === marketplace.market.countryCode
  )
})

const shouldDisplaySellerCountry = computed(() => {
  return (
    !!props.selectedOffer.merchant &&
    !!props.selectedOffer.merchant.country.code &&
    !shouldDisplaySellerFlag.value
  )
})

const sellerpageUrl = computed(() => {
  const { merchant } = props.selectedOffer

  if (!merchant?.merchantUrl || !merchant?.merchantId) {
    return undefined
  }

  try {
    const { pathname } = new URL(merchant.merchantUrl)

    // Extract merchant slug from URL pattern: /{locale}/s/{merchantName}/{uuid}
    // or /s/{merchantName}/{uuid}
    const merchantSlugMatch = pathname.match(/\/s\/([^/]+)\//)

    if (merchantSlugMatch && merchantSlugMatch[1]) {
      const merchantSlug = merchantSlugMatch[1]

      return resolveLocalizedRoute({
        name: PRODUCT_LIST_ROUTES.SELLER_PRODUCT_LIST,
        params: {
          merchantName: merchantSlug,
          uuid: merchant.merchantId,
        },
      })
    }
  } catch {
    return merchant.merchantUrl
  }

  return merchant.merchantUrl
})

function onSellerClick() {
  trackClick({
    zone: 'recap',
    name: 'seller',
    value:
      props.selectedOffer.merchant?.country.code ||
      props.selectedOffer.merchant?.country ||
      '',
  })
}
</script>
